import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SubscriptionService } from '../services/subscription.service';
import { CompaniesService } from '../services/companies.service';
import { SetupProgressService } from '../services/setup-progress.service';
import { IntegrationsService } from '../services/integrations.service';
import { AuthService } from '../services/auth.service';
import { SubscriptionGraceService } from '../services/subscription-grace.service';

/**
 * Hard ceiling on how long a login may wait for the billing API.
 *
 * The guard blocks the dashboard on this call, so a slow billing service is
 * indistinguishable from a broken app to the user. `SubscriptionService` already
 * bounds itself, but its budget has to cover the billing page too; login gets the
 * stricter deadline and falls through if the answer isn't back in time.
 */
const SUBSCRIPTION_DEADLINE_MS = 6000;

/**
 * Load the subscription without letting it stall the login.
 *
 * @returns true when the subscription could not be DETERMINED (error or deadline).
 *          The caller must then decide from prior evidence — see the grace check in
 *          the guard — and must NOT simply allow, which would let anyone who can
 *          stall this request into the app.
 */
async function loadSubscriptionWithDeadline(
  subscriptionService: SubscriptionService,
  companyId: string
): Promise<boolean> {
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;

  try {
    const outcome = await Promise.race([
      subscriptionService.loadSubscription(companyId).then(() => 'loaded' as const),
      new Promise<'deadline'>(resolve => {
        deadlineTimer = setTimeout(() => resolve('deadline'), SUBSCRIPTION_DEADLINE_MS);
      })
    ]);

    if (outcome === 'deadline') {
      console.warn(
        `🛡️ OnboardingGuard: Subscription lookup exceeded ${SUBSCRIPTION_DEADLINE_MS}ms; continuing without it`
      );
      return true;
    }

    return subscriptionService.didLastLoadFail();
  } catch (error) {
    console.error('Error loading subscription in OnboardingGuard:', error);
    return true;
  } finally {
    clearTimeout(deadlineTimer);
  }
}

/**
 * OnboardingGuard - Protects routes that require subscription setup
 *
 * Redirects users to tier selection if they don't have an active subscription.
 * Used for the main app routes after login.
 */
export const OnboardingGuard: CanActivateFn = async (route, state) => {
  console.log('🛡️ OnboardingGuard: Starting guard check');
  // All inject() calls must be at the top, before any async operations
  const router = inject(Router);
  const subscriptionService = inject(SubscriptionService);
  const companiesService = inject(CompaniesService);
  const setupProgressService = inject(SetupProgressService);
  const integrationsService = inject(IntegrationsService);
  const authService = inject(AuthService);
  const subscriptionGrace = inject(SubscriptionGraceService);

  try {
    // Ensure companies are loaded before checking
    console.log('🛡️ OnboardingGuard: hasCompaniesLoaded =', companiesService.hasCompaniesLoaded());
    if (!companiesService.hasCompaniesLoaded()) {
      try {
        console.log('🛡️ OnboardingGuard: Loading companies...');
        await companiesService.initializeCompanySelection();
        console.log('🛡️ OnboardingGuard: Companies loaded');
      } catch (error) {
        console.log('🛡️ OnboardingGuard: Could not load companies, likely a new user', error);
      }
    }

    // Get selected company
    const selectedCompany = companiesService.getSelectedCompany();
    console.log('🛡️ OnboardingGuard: selectedCompany =', selectedCompany);

    if (!selectedCompany) {
      // No company selected - check if user has any companies
      const companies = companiesService.getAvailableCompanies();
      console.log('🛡️ OnboardingGuard: availableCompanies =', companies?.length || 0);

      if (!companies || companies.length === 0) {
        // IMPORTANT: an empty company list can mean one of two very different things:
        //   1) the load FAILED (e.g. backend 502) — we simply couldn't read the
        //      user's companies, and
        //   2) the load SUCCEEDED and the user genuinely has zero companies (new user).
        // Only case (2) should go to /complete-profile. Treating case (1) the same
        // way sends an already-onboarded user into an infinite
        // /dashboard <-> /complete-profile loop. Fail OPEN on a load failure,
        // consistent with the guard's fail-open philosophy elsewhere.
        if (companiesService.didLastCompanyLoadFail()) {
          console.warn('🛡️ OnboardingGuard: Company load failed (transient backend error); ' +
            'NOT redirecting to /complete-profile to avoid an onboarding loop. Allowing navigation.');
          return true;
        }

        // New user with no companies - redirect to complete-profile first
        console.log('🛡️ OnboardingGuard: No companies found, redirecting to /complete-profile');
        return router.createUrlTree(['/complete-profile']);
      }

      // User has companies but none selected - allow navigation (will be prompted to select)
      console.log('🛡️ OnboardingGuard: Has companies but none selected, allowing navigation');
      return true;
    }

    // Check if user has billing permissions for this company
    // Invited/non-billing users (Sub-Account Admin, Manager, Viewer, etc.) skip ALL
    // subscription and setup checks — billing and setup are the company owner's responsibility.
    // Without this, invited users get stuck on /select-plan where they can't do anything.
    const userPermissions = selectedCompany.userPermissions || [];
    const canManageBilling = userPermissions.includes('manage_billing');

    if (!canManageBilling) {
      console.log('🛡️ OnboardingGuard: Non-billing user (role: %s), skipping onboarding checks', selectedCompany.userRole);
      return true;
    }

    // --- Below here: only runs for Company Owners / Organization Admins ---

    // Subscription and setup progress are independent reads. Fetching them together
    // removes a sequential round trip from every login — and on this stack each of
    // those round trips is a cold Lambda, so the saving is ~1s, not ~50ms.
    const [subscriptionLoadFailed] = await Promise.all([
      subscriptionService.getSubscription()
        ? Promise.resolve(false)
        : loadSubscriptionWithDeadline(subscriptionService, selectedCompany.companyId),
      setupProgressService.getProgress()
        ? Promise.resolve(null)
        : setupProgressService.loadProgress().catch(error => {
            console.error('Error loading setup progress:', error);
            return null;
          })
    ]);

    // A subscription we could not READ is not the same as a company that HAS no
    // subscription — loadSubscription() returns null for both — but "allow anyone
    // whose billing call failed" is not the answer either. Stalling that call is
    // trivial (throttling, a firewall DROP, a hosts-file blackhole), which would
    // hand the app to anyone who never subscribed or has since churned.
    //
    // Split it by evidence instead: proceed only for a user we have POSITIVELY seen
    // entitled recently, on this company. No record — new user, cleared browser,
    // different person on this machine — falls through to the normal checks below,
    // which send them to /select-plan.
    if (subscriptionLoadFailed) {
      if (subscriptionGrace.mayProceedOnGrace(selectedCompany.companyId)) {
        console.warn('🛡️ OnboardingGuard: Subscription unreadable; proceeding on last-known-good entitlement');
        return true;
      }
      console.warn('🛡️ OnboardingGuard: Subscription unreadable and no prior entitlement on record; treating as unsubscribed');
      return router.createUrlTree(['/select-plan']);
    }

    // Check subscription status
    const status = subscriptionService.getSubscriptionStatus();

    // Record the outcome of this SUCCESSFUL read as the new last-known-good. The
    // negative case matters as much as the positive one: once a subscription is
    // seen as canceled, a later unreadable response fails closed rather than
    // resurrecting an entitlement that has since lapsed.
    subscriptionGrace.record(selectedCompany.companyId, status.canAccessPlatform, status.status);

    // Redirect to tier selection if no active subscription (must select plan first)
    if (!status.hasSubscription || status.status === 'none') {
      console.log('🛡️ OnboardingGuard: No subscription, redirecting to /select-plan');
      return router.createUrlTree(['/select-plan']);
    }

    // Redirect pending subscriptions (checkout started but not completed) to tier selection
    if (status.status === 'pending' as any) {
      console.log('🛡️ OnboardingGuard: Subscription pending (checkout incomplete), redirecting to /select-plan');
      return router.createUrlTree(['/select-plan']);
    }

    // Redirect trialing users without a payment method to tier selection
    // so they can enter their card (card is required even for trial)
    if (status.status === 'trialing') {
      const sub = subscriptionService.getSubscription();
      if (sub && !sub.hasPaymentMethod) {
        console.log('🛡️ OnboardingGuard: Trialing but no payment method, redirecting to /select-plan');
        return router.createUrlTree(['/select-plan']);
      }
    }

    // User has subscription - now check if they need to complete setup
    if (status.canAccessPlatform) {
      // Already resolved above, in parallel with the subscription lookup.
      const setupProgress = setupProgressService.getProgress();
      const hasCompletedSetup = setupProgress?.completedAt;
      const hasSkippedSetup = setupProgress?.skippedAt;

      if (!hasCompletedSetup && !hasSkippedSetup) {
        // Check if user has any integrations (indicates they're not brand new)
        try {
          const integrationsResponse = await integrationsService.getIntegrations(
            authService.userId(),
            selectedCompany.companyId
          );
          const integrations = integrationsResponse?.data || [];
          const hasAnyIntegration = integrations.some((i: any) => i.connected || i.enabled);

          // If no integrations and no setup progress, redirect to setup wizard
          if (!hasAnyIntegration) {
            console.log('🛡️ OnboardingGuard: User has subscription but needs setup, redirecting to /setup');
            return router.createUrlTree(['/setup']);
          }
        } catch (error) {
          console.error('Error checking integrations in OnboardingGuard:', error);
          // Continue to app if we can't check integrations
        }
      }

      // User has subscription and either completed setup or has integrations
      return true;
    }

    // Redirect to billing if subscription is past due or canceled
    if (status.status === 'past_due' || status.status === 'canceled') {
      console.log('🛡️ OnboardingGuard: Subscription issue, redirecting to /billing');
      return router.createUrlTree(['/billing']);
    }

    // Default: allow access
    return true;

  } catch (error) {
    console.error('OnboardingGuard error:', error);
    // Fail open - allow access if there's an error checking subscription
    return true;
  }
};

/**
 * SubscriptionRequiredGuard - Stricter version that requires active subscription
 *
 * Use this for premium features that should never be accessible without subscription.
 */
export const SubscriptionRequiredGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const subscriptionService = inject(SubscriptionService);
  const companiesService = inject(CompaniesService);

  try {
    const selectedCompany = companiesService.getSelectedCompany();

    if (!selectedCompany) {
      return router.createUrlTree(['/login']);
    }

    // Ensure subscription is loaded
    let subscription = subscriptionService.getSubscription();
    if (!subscription) {
      subscription = await subscriptionService.loadSubscription(selectedCompany.companyId);
    }

    const status = subscriptionService.getSubscriptionStatus();

    if (status.canAccessPlatform) {
      return true;
    }

    // Store intended destination for redirect after subscription
    sessionStorage.setItem('redirectAfterSubscription', state.url);

    if (!status.hasSubscription) {
      return router.createUrlTree(['/select-plan']);
    }

    return router.createUrlTree(['/billing']);

  } catch (error) {
    console.error('SubscriptionRequiredGuard error:', error);
    return router.createUrlTree(['/select-plan']);
  }
};

/**
 * TierSelectionGuard - Prevents access to tier selection if already subscribed
 *
 * Redirects subscribed users back to dashboard.
 */
export const TierSelectionGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const subscriptionService = inject(SubscriptionService);
  const companiesService = inject(CompaniesService);

  try {
    // Ensure companies are loaded before checking (matches OnboardingGuard behavior)
    if (!companiesService.hasCompaniesLoaded()) {
      try {
        await companiesService.initializeCompanySelection();
      } catch (error) {
        // Can't load companies - allow access (likely a new user)
        return true;
      }
    }

    const selectedCompany = companiesService.getSelectedCompany();

    if (!selectedCompany) {
      // Allow access to tier selection for new users
      return true;
    }

    // Non-billing users (invited users) cannot select a plan — redirect to dashboard
    const userPermissions = selectedCompany.userPermissions || [];
    if (!userPermissions.includes('manage_billing')) {
      console.log('TierSelectionGuard: Non-billing user, redirecting to dashboard');
      return router.createUrlTree(['/dashboard']);
    }

    let subscription = subscriptionService.getSubscription();
    if (!subscription) {
      try {
        subscription = await subscriptionService.loadSubscription(selectedCompany.companyId);
      } catch {
        // If we can't load subscription, allow access to tier selection
        return true;
      }
    }

    const status = subscriptionService.getSubscriptionStatus();

    // Allow trial users to access tier selection to upgrade
    if (status.status === 'trialing') {
      console.log('TierSelectionGuard: User is on trial, allowing access to upgrade');
      return true;
    }

    // If user already has an active (non-trial) subscription, redirect to dashboard
    if (status.hasSubscription && status.canAccessPlatform && status.status === 'active') {
      console.log('TierSelectionGuard: Already subscribed (active), redirecting to dashboard');
      return router.createUrlTree(['/dashboard']);
    }

    return true;

  } catch (error) {
    console.error('TierSelectionGuard error:', error);
    return true;
  }
};
